// Pure timesheet helpers (no React), shared by the calendar, the monthly sheet and the reports.

export const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function toLocalDateStr(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

const startKey = (entry) => toLocalDateStr(entry.date);
const endKey = (entry) => (entry.endDate ? toLocalDateStr(entry.endDate) : startKey(entry));

/** Does this entry (one day, or a date range) cover the YYYY-MM-DD day `dateKey`? */
export const coversDay = (entry, dateKey) => dateKey >= startKey(entry) && dateKey <= endKey(entry);

/**
 * An entry's hours are logged once, on its start day. The other days of a multi-day entry
 * list it without hours, so a day's total (and a month's) counts each entry exactly once.
 */
export const hoursOnDay = (entry, dateKey) => (dateKey === startKey(entry) ? entry.hours || 0 : 0);

/** Hours logged in a month (1-12): entries that start in it, each counted once. */
export function monthTotalHours(entries, year, month) {
  const prefix = `${year}-${String(month).padStart(2, "0")}-`;
  return entries.reduce(
    (sum, e) => (startKey(e).startsWith(prefix) ? sum + (e.hours || 0) : sum),
    0
  );
}

/**
 * The monthly sheet (month 1-12): one row per entry per calendar day; days with no entries get
 * a blank row. An entry with an endDate appears on every day of its range that falls in the
 * month (including one that started the month before), but only its start day carries its
 * hours: the rows of the following days are marked `continued`.
 */
export function buildMonthlyRows(entries, year, month) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const rows = [];

  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const dateObj = new Date(year, month - 1, d);
    const dayName = DAYS[dateObj.getDay()];
    const dateLabel = `${String(d).padStart(2, "0")} ${MONTHS[month - 1].slice(0, 3)}`;

    const dayEntries = entries.filter((e) => coversDay(e, dateStr));
    const dayTotal = dayEntries.reduce((s, e) => s + hoursOnDay(e, dateStr), 0);

    if (dayEntries.length === 0) {
      rows.push({
        dateLabel,
        dayName,
        isFirst: true,
        isLast: true,
        entry: null,
        continued: false,
        dayTotal: 0,
      });
    } else {
      dayEntries.forEach((entry, i) => {
        rows.push({
          dateLabel: i === 0 ? dateLabel : "",
          dayName: i === 0 ? dayName : "",
          isFirst: i === 0,
          isLast: i === dayEntries.length - 1,
          entry,
          continued: dateStr !== startKey(entry),
          dayTotal,
        });
      });
    }
  }
  return rows;
}

/**
 * The create/update body for the entry form. Editing keeps the entry's own start date: the
 * dialog may have been opened from a later day of a multi-day entry, and that day is not the
 * entry's start.
 */
export function entryPayload(form, { editEntry, dateStr, targetUserId }) {
  const payload = {
    date: editEntry ? editEntry.date : dateStr,
    projectId: form.projectId === "other" ? null : form.projectId,
    description: form.description,
    hours: form.hours ? Number(form.hours) : null,
    endDate: form.endDate || null,
  };
  if (targetUserId) payload.userId = targetUserId;
  return payload;
}

/** CSV text with every field quoted, so names and projects containing commas stay one column. */
export function toCsv(rows) {
  const cell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  return rows.map((row) => row.map(cell).join(",")).join("\n");
}

/** Days an entry spans, start and end day included, from "YYYY-MM-DD" strings (1 if no end). */
export function spanDays(startStr, endStr) {
  if (!startStr || !endStr) return 1;
  const [sy, sm, sd] = startStr.split("-").map(Number);
  const [ey, em, ed] = endStr.split("-").map(Number);
  const days = Math.round((Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)) / 86400000) + 1;
  return Number.isFinite(days) && days > 0 ? days : 1;
}

/**
 * Message for the form's Hours field, or "". Hours are the entry's total, so the cap is 24 per
 * day it spans. When editing, unchanged hours are not checked, so an old over-cap entry can
 * still have its description changed.
 */
export function hoursProblem(form, { startStr, editEntry }) {
  if (form.hours === "" || form.hours === null || form.hours === undefined) return "";
  const hours = Number(form.hours);
  if (editEntry && Number(editEntry.hours) === hours) return "";
  const max = 24 * spanDays(startStr, form.endDate);
  if (!Number.isFinite(hours) || hours <= 0 || hours > max) {
    return `Hours must be more than 0 and at most ${max} for ${
      max === 24 ? "a single day" : "an entry spanning these days"
    }.`;
  }
  return "";
}

/** Number of entries that start in a month (1-12): the same entries monthTotalHours adds up. */
export function monthEntryCount(entries, year, month) {
  const prefix = `${year}-${String(month).padStart(2, "0")}-`;
  return entries.filter((e) => startKey(e).startsWith(prefix)).length;
}
